// #2193 / #2355 — "do not show this message again" preference for the Start
// Scoring confirm modal (see ScoringForm.jsx's handleStartScoringClick).
//
// #2193 originally persisted this to localStorage (same shape as
// sheetFieldOrder.js), which meant a scribe who ticked the box by reflex on
// day one could never see the modal again on that kiosk — there is no
// scribe-visible affordance to undo it, and scribe-fe is a kiosk with no
// escape hatches. Jaybill decided (2026-09-07, card #2355 comment):
// suppression is PER-SESSION, not persistent. No restore affordance is
// needed because there is nothing durable to restore from — a new session
// brings the modal back on its own.
//
// "Per session" and "per page load" are different promises. This kiosk
// reloads mid-ride (see #901's reopened-after-sign restore, and general PWA
// reload behavior), and a scribe re-seeing the modal on every reload would
// be its own bug. sessionStorage survives a same-tab reload but is cleared
// when the tab/window closes, which matches "per session" the way a human
// would read it — so that's what this uses (not in-memory/module state,
// which would not survive the reloads this kiosk actually does).
//
// try/catch'd so a browser with storage disabled (private mode) degrades to
// "always show the modal" rather than throwing.
const STORAGE_KEY = "scribe-fe.suppressStartScoringConfirm";

// A device that ran a #2193 build before this fix may still be carrying the
// OLD persistent localStorage key. That value must never be honoured again
// (a scribe on such a device would otherwise stay suppressed forever, which
// is exactly the bug this card fixes) — so it is actively deleted the first
// time this module loads on that device, rather than merely left unread.
try {
  localStorage.removeItem(STORAGE_KEY);
} catch {
  // Storage unavailable (private browsing) — nothing to clean up.
}

/** @returns {boolean} true if the scribe already ticked "don't show again" this session. */
export function getStartScoringConfirmSuppressed() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** @param {boolean} suppressed */
export function setStartScoringConfirmSuppressed(suppressed) {
  try {
    if (suppressed) {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private browsing) — non-fatal; the preference
    // just won't survive the reload, and the modal shows every time.
  }
}
